import * as THREE from 'three';
import { CONFIG } from '../config.js';
import { getSharedBinaryAtlas } from '../rendering/binary/sharedBinaryResources.js';
import { BinaryMaterial } from '../rendering/binary/BinaryMaterial.js';
import { NPC, NPC_STATE } from './NPC.js';
import { NPCSpawner } from './NPCSpawner.js';
import { NPCSpatialIndex } from './NPCSpatialIndex.js';
import { debugLog } from '../debug/debugLog.js';

/**
 * Pooled crowd simulation for Matrix pedestrians.
 */
export class NPCManager {
  /** @param {{ world: import('../world/World.js').World }} deps */
  constructor({ world }) {
    this.world = world;
    this.config = CONFIG.npc;

    this.group = new THREE.Group();
    this.group.name = 'NPCCrowd';

    /** @type {NPC[]} */
    this._pool = [];
    /** @type {NPC[]} */
    this._active = [];

    this._atlas = null;
    this._material = null;
    this._spawner = null;
    this._graph = null;
    this._cellIdNext = 0;
    this._maxColumns = 8;
    this._initialized = false;
    this._framePhase = 0;
    this._spatial = new NPCSpatialIndex(2.8);
    this._queryRadius =
      Math.max(this.config.avoidanceRadius, this.config.playerAvoidRadius) * 2.2;

    this.lodStats = { full: 0, medium: 0, far: 0, static: 0 };
    this._worldFrozen = false;
    this._footstepsThisFrame = 0;
    /** @type {((npc: import('./NPC.js').NPC, event: import('../player/FootstepController.js').FootstepEvent) => void) | null} */
    this._footstepHandler = null;
    /** @type {{ x: number; z: number } | null} */
    this._footstepListenerPos = null;
  }

  /**
   * @param {((npc: import('./NPC.js').NPC, event: import('../player/FootstepController.js').FootstepEvent) => void) | null} handler
   */
  setFootstepHandler(handler) {
    this._footstepHandler = handler;
    const wrapped = handler
      ? (npc, event) => {
          const listen = this._footstepListenerPos;
          if (!listen) return;

          const dx = npc.position.x - listen.x;
          const dz = npc.position.z - listen.z;
          const dist = Math.hypot(dx, dz);
          const maxDist = CONFIG.audio.npcFootsteps.maxDistance;
          if (dist > maxDist) return;

          const cfg = CONFIG.audio.npcFootsteps;
          const nearDist = cfg.nearPriorityDistance ?? 14;
          const cap =
            dist <= nearDist
              ? (cfg.nearMaxPerFrame ?? cfg.maxPerFrame)
              : cfg.maxPerFrame;
          if (this._footstepsThisFrame >= cap) return;

          this._footstepsThisFrame += 1;
          handler(npc, event);
        }
      : null;
    for (const npc of this._pool) {
      npc.setFootstepHandler(wrapped);
    }
  }

  /** @param {boolean} frozen */
  setWorldFrozen(frozen) {
    this._worldFrozen = frozen;
  }

  get activeCount() {
    return this._active.length;
  }

  /**
   * @param {{ x: number; z: number }} position
   * @param {number} radius
   */
  countWithinRadius(position, radius) {
    const rSq = radius * radius;
    let count = 0;
    for (const npc of this._active) {
      if (!npc.active) continue;
      const dx = npc.position.x - position.x;
      const dz = npc.position.z - position.z;
      if (dx * dx + dz * dz <= rSq) count += 1;
    }
    return count;
  }

  initialize() {
    if (this._initialized) return;

    this._graph = this.world.getNavigationGraph();
    if (!this._graph || this._graph.nodes.length === 0) {
      console.warn('[NPCManager] Navigation graph missing or empty.');
      return;
    }

    this._atlas = getSharedBinaryAtlas();
    this._material = new BinaryMaterial({
      atlas: this._atlas,
      palette: 'accent',
      overrides: {
        glowStrength: CONFIG.luminance.npcGlowStrength,
        flickerStrength: 0.16,
        scrollSpeed: 3.6,
        pulseSpeed: 1.35,
      },
    });
    this._spawner = new NPCSpawner(
      this._graph,
      this.config.seed,
      CONFIG.city,
      this.config,
    );

    const targetCount = this.config.count;
    for (let i = 0; i < targetCount; i += 1) {
      const cellIdBase = this._cellIdNext;
      const npc = new NPC({
        id: i,
        material: this._material,
        graph: this._graph,
        cellIdBase,
        seed: this.config.seed + i * 17,
        config: this.config,
      });
      this._cellIdNext += npc.body.cellsUsed;
      const cols = npc.body.surfaces.reduce(
        (m, s) => Math.max(m, s.columns),
        1,
      );
      this._maxColumns = Math.max(this._maxColumns, cols);
      this._pool.push(npc);
      this.group.add(npc.root);
      npc.deactivate();
    }

    this._material.setColumns(this._maxColumns);

    for (let i = 0; i < targetCount; i += 1) {
      this._activateFromPool();
    }

    this._initialized = true;
    debugLog('[NPCManager]', {
      active: this._active.length,
      navNodes: this._graph.nodes.length,
      glyphCellBase: this._cellIdNext,
    });
  }

  /** @param {number} count */
  setActiveCount(count) {
    if (!this._initialized) return;
    const target = Math.max(0, Math.min(count, this._pool.length));
    while (this._active.length > target) {
      const npc = this._active.pop();
      npc?.deactivate();
    }
    while (this._active.length < target) {
      this._activateFromPool();
    }
  }

  /**
   * @param {number} delta
   * @param {THREE.Vector3} playerPosition
   */
  update(delta, playerPosition) {
    if (!this._initialized) return;

    this._footstepsThisFrame = 0;
    this._footstepListenerPos = playerPosition;

    if (!CONFIG.graphics?.staticBinaryGlyphs) {
      this._material.update(delta);
    }

    if (this._worldFrozen) {
      return;
    }

    const graph = this.world.getNavigationGraph();
    if (graph && graph !== this._graph) {
      this._graph = graph;
      this._spawner = new NPCSpawner(
        graph,
        this.config.seed,
        CONFIG.city,
        this.config,
      );
    }

    const perf = CONFIG.performance;
    const farDist =
      perf.npcFarDistance > 0
        ? perf.npcFarDistance
        : (CONFIG.streaming?.simulationDistance ?? 90);
    const fullSq = perf.npcFullDistance ** 2;
    const medSq = perf.npcMediumDistance ** 2;
    const farSq = farDist ** 2;
    const cullSq = perf.npcCullDistance ** 2;
    const medStride = perf.npcMediumThinkStride ?? 2;

    this.lodStats.full = 0;
    this.lodStats.medium = 0;
    this.lodStats.far = 0;
    this.lodStats.static = 0;

    this._spatial.rebuild(this._active);
    this._framePhase = (this._framePhase + 1) % 4096;
    this._applyThinkStride();

    this._updateGroups();

    for (const npc of this._active) {
      const dx = npc.position.x - playerPosition.x;
      const dz = npc.position.z - playerPosition.z;
      const distSq = dx * dx + dz * dz;

      if (distSq > cullSq) {
        npc.updateStatic(delta, this._framePhase);
        this.lodStats.static += 1;
        continue;
      }

      if (distSq > farSq) {
        npc.updateSimplified(delta);
        this.lodStats.far += 1;
        continue;
      }

      npc.graph = this._graph;

      if (distSq > medSq) {
        npc.updateReduced(delta);
        this.lodStats.medium += 1;
        continue;
      }

      if (distSq > fullSq) {
        if ((this._framePhase + npc.updatePhase) % medStride !== 0) {
          npc.updateReduced(delta);
        } else {
          const neighbors = this._spatial.query(
            npc.position.x,
            npc.position.z,
            this._queryRadius,
          );
          npc.update(
            delta,
            this._spawner,
            playerPosition,
            neighbors,
            this._framePhase,
          );
        }
        this.lodStats.medium += 1;
        continue;
      }

      const neighbors = this._spatial.query(
        npc.position.x,
        npc.position.z,
        this._queryRadius,
      );
      npc.update(
        delta,
        this._spawner,
        playerPosition,
        neighbors,
        this._framePhase,
      );
      this.lodStats.full += 1;
    }
  }

  dispose() {
    for (const npc of this._pool) npc.dispose();
    this._pool = [];
    this._active = [];
    this._material?.dispose();
    this.group.clear();
    this._initialized = false;
  }

  _applyThinkStride() {
    const n = this._active.length;
    this.config.thinkStride = Math.max(
      CONFIG.npc.thinkStride,
      Math.ceil(n / 100),
    );
  }

  _activateFromPool() {
    const npc = this._pool.find((n) => !n.active);
    if (!npc || !this._spawner) return;

    const { nodeId, speed } = this._spawner.pickSpawnParams(npc.personality);
    const node = this._graph.getNode(nodeId);
    npc.spawn(node.x, node.z, speed, nodeId);
    this._active.push(npc);
    this._maybeFormGroup(npc);
  }

  /** @param {NPC} npc */
  _maybeFormGroup(npc) {
    const { groupChance, maxGroupSize } = this.config;
    if (npc.personality.groupAffinity < 0.55) return;
    if ((npc.id * 31 + npc.personality.timerJitter * 10) % 100 > groupChance * 100) {
      return;
    }

    /** @type {NPC[]} */
    const neighbors = [];
    for (const other of this._active) {
      if (other === npc || !other.active) continue;
      const d = Math.hypot(
        other.position.x - npc.position.x,
        other.position.z - npc.position.z,
      );
      if (d < 4.5) neighbors.push(other);
    }
    let leader = null;
    for (const other of neighbors) {
      if (other === npc || !other.active) continue;
      if (other.personality.isGroupLeader && other.personality.groupMemberIds.length < maxGroupSize - 1) {
        leader = other;
        break;
      }
    }

    if (leader) {
      npc.personality.groupLeaderId = leader.id;
      leader.personality.groupMemberIds.push(npc.id);
      npc.followLeader(leader);
      return;
    }

    if (npc.personality.groupAffinity > 0.72 && neighbors.length >= 2) {
      npc.personality.isGroupLeader = true;
      let members = 0;
      for (const other of neighbors) {
        if (other === npc || !other.active || other.personality.groupLeaderId != null) continue;
        if (other.personality.groupAffinity < 0.45) continue;
        other.personality.groupLeaderId = npc.id;
        npc.personality.groupMemberIds.push(other.id);
        other.followLeader(npc);
        members += 1;
        if (members >= maxGroupSize - 1) break;
      }
      if (members === 0) npc.personality.isGroupLeader = false;
    }
  }

  _updateGroups() {
    for (const npc of this._active) {
      if (!npc.active || !npc.personality.isGroupLeader) continue;
      for (const memberId of npc.personality.groupMemberIds) {
        const follower = this._pool[memberId];
        if (!follower?.active) continue;
        if (
          follower.state === NPC_STATE.WAITING ||
          follower.state === NPC_STATE.IDLE
        ) {
          continue;
        }
        if (follower.path.length === 0 && npc.path.length > 0) {
          follower.followLeader(npc);
        }
      }
    }
  }
}
